import { ACDSessionManager } from '@nice-devone/core-sdk';
import { logger } from '../../../utils/logger';
import { cxoneSdkState } from '../state';
import { SessionConflictError, type VoicePreference } from '../types';
import { buildStartSessionRequest } from './build-start-session-request';
import { forceEndAllSessions } from './force-logout';
import { tryJoinExistingSession } from './join-session';
import {
  isInvalidSessionError,
  isSessionConflictError,
  isSessionConflictResponse,
} from './session-conflict.util';
import { extractSessionId, persistSessionId } from './session-id.util';

/**
 * Start agent session using ACDSessionManager
 *
 * @param preference - Voice preference from VoicePreferences component
 * @throws SessionConflictError when there's an active session elsewhere
 */
export async function startAgentSession(preference?: VoicePreference): Promise<void> {
  logger.debug('[CXone SDK] Starting agent session with preference:', preference);

  const acdSession = ACDSessionManager.instance;
  const agentId = cxoneSdkState.storedUserInfo?.icAgentId;

  if (agentId && (await tryJoinExistingSession(acdSession, agentId))) {
    return;
  }

  const startSessionRequest = await buildStartSessionRequest(preference);
  logger.debug('[CXone SDK] StartSession request:', startSessionRequest);

  try {
    const response = await acdSession.startSession(startSessionRequest);

    if (response && 'errorCode' in response) {
      const errorResponse = response as {
        errorCode: string;
        errorMessage?: string;
        status?: number;
        error_description?: string;
      };

      if (isSessionConflictResponse(errorResponse)) {
        logger.debug('[CXone SDK] Session conflict detected - agent has active session elsewhere');
        throw new SessionConflictError();
      }

      throw new Error(
        `Session start failed: ${
          errorResponse.errorMessage || errorResponse.error_description || errorResponse.errorCode
        }`
      );
    }

    logger.debug('[CXone SDK] Agent session started successfully:', response);
    persistSessionId(acdSession, extractSessionId(response));
  } catch (error: unknown) {
    if (error instanceof SessionConflictError) {
      throw error;
    }

    if (isSessionConflictError(error)) {
      logger.debug('[CXone SDK] Session conflict detected - agent has active session elsewhere');
      throw new SessionConflictError();
    }

    if (isInvalidSessionError(error)) {
      logger.debug('[CXone SDK] Invalid session detected, ending and retrying...');
      try {
        await forceEndAllSessions();
        const retryResponse = await acdSession.startSession(startSessionRequest);
        if (retryResponse && 'errorCode' in retryResponse) {
          throw new Error(
            `Session start failed after cleanup: ${
              (retryResponse as { errorCode: string }).errorCode
            }`
          );
        }
        logger.debug(
          '[CXone SDK] Agent session started successfully after cleanup:',
          retryResponse
        );
        persistSessionId(acdSession, extractSessionId(retryResponse));
        return;
      } catch (retryError) {
        if (retryError instanceof SessionConflictError) {
          throw retryError;
        }
        logger.error('[CXone SDK] Failed to start session after cleanup:', retryError);
        throw retryError;
      }
    }

    logger.error('[CXone SDK] Failed to start agent session:', error);
    throw error;
  }
}
